import { useEffect, useState, forwardRef } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { useTranslation } from '../../context/translationContext';
import { getCurrentLangFormat } from '../../types/core/date.types';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

interface Props {
  placeholder?: string;
  onChange: (value: Date | null) => void;
  value: Date | null;
  isRequired?: boolean;
  className?: string;
  format?: string;
  readOnly?: boolean;
}

const CustomInput = forwardRef<HTMLInputElement, any>(function CustomInput(
  { value, onClick, className, readOnly }: any,
  ref,
) {
  return (
    <div className="flex justify-between items-center w-full">
      <input
        ref={ref}
        type="text"
        value={value ?? ''}
        onClick={onClick}
        onChange={() => null}
        className={`rounded-md w-full appearance-none block px-2 py-0.5 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:z-10 ${className}`}
        readOnly={readOnly}
      />
      {!readOnly && (
        <CalendarDaysIcon
          className="w-5 h-5 absolute py-0 right-2 text-gray-600 z-10 cursor-pointer"
          onClick={onClick}
        />
      )}
    </div>
  );
});

CustomInput.displayName = 'CustomInput';

/**
 * General component for date | datetime input
 */
export const Datepicker = ({
  placeholder,
  onChange,
  value,
  isRequired,
  className,
  format,
  readOnly,
}: Props) => {
  const [formatLoc, setFormatLoc] = useState<string>();
  const { lang } = useTranslation();

  useEffect(() => {
    if (format || !lang) return;
    setFormatLoc(getCurrentLangFormat(lang));
  }, [lang]);

  return (
    <>
      <DatePicker
        dateFormat={format ?? formatLoc}
        selected={value}
        onChange={(e) => e && onChange(e)}
        placeholderText={placeholder}
        showPopperArrow={false}
        required={isRequired}
        readOnly={readOnly}
        customInput={<CustomInput className={className} readOnly={readOnly} />}
      />
    </>
  );
};
